import Box from 'assets/components/box/Box';
import Button from 'assets/components/button/Button';
import ThemeButtonCircle from 'assets/components/button/themes/Theme.Button.Circle';
import DataMap from 'assets/components/dataMap/DataMap';
import { useModalForm } from 'assets/components/form/modalForm/ModalForm';
import Icon from 'assets/components/icon/Icon';
import CheckInput from 'assets/components/inputs/check/CheckInput';
import { inputStyles } from 'assets/components/inputs/Input';
import CheckWrapper from 'assets/components/inputs/wrappers/CheckWrapper';
import ThemeTableSimple from 'assets/components/table/themes/Theme.Table.Simple';
import ThemeTagRounded from 'assets/components/tag/themes/Theme.Tag.Rounded';
import TextBox, { textBoxStyles } from 'assets/components/textBox/TextBox';
import useApi from 'assets/hooks/api/useApi';
import { getLocales, Language } from 'assets/locales/Locale';
import Theme from 'assets/themes/Theme.Common';
import { cloneDeep, intersectionBy } from 'lodash';
import LossCode from 'models/claims/lossCode/Model.LossCode';
import { useState } from 'react';
import ClaimTypeJobItemTemplatesEditForm from './ClaimType.Edit.JobItemTemplatesForm';
import { getLineItemLabel } from 'module/claims/helper/LineItem.Label';

export default function ClaimTypeEditJobItemTemplates() {
  const { lang, currentLanguage } = getLocales();
  const [expandedJobItemId, setExpandedJobItemId] = useState<number>();
  const { form } = useModalForm<Api.Core.ClaimType.Save.IRequest>();
  const [currentJobItemTemplate, setCurrentJobItemTemplate] =
    useState<Utils.FormData<Model.IClaimTypeJobItemTemplate>>(null);

  const deleteJobItemTemplate = (index: number) => {
    const updatedJobItemTemplates = form.data?.claimTypeJobItemTemplates?.filter((_, idx) => idx !== index);
    form.update({ claimTypeJobItemTemplates: updatedJobItemTemplates });
  };

  const { payload: lossCodePayload } = useApi({
    action: LossCode.list,
    body: { excludeDisabled: true },
  });

  const itemTemplateLossCodes = intersectionBy(lossCodePayload?.data, form.data.lossCodes, 'id');

  return (
    <>
      {currentJobItemTemplate && (
        <ClaimTypeJobItemTemplatesEditForm
          data={currentJobItemTemplate}
          onCancel={() => setCurrentJobItemTemplate(null)}
          onSave={(newJobItemTemplate) => {
            const cloned = cloneDeep(form.data?.claimTypeJobItemTemplates) ?? [];
            if (newJobItemTemplate.id == null) {
              newJobItemTemplate.id = -1 * (cloned.length || 0);
            }
            const index = cloned.findIndex((it) => it?.id == newJobItemTemplate?.id);
            if (index >= 0) {
              cloned[index] = newJobItemTemplate;
            } else {
              cloned.push(newJobItemTemplate);
            }
            form.update({ claimTypeJobItemTemplates: cloned });
            setCurrentJobItemTemplate(null);
          }}
          lossCodes={itemTemplateLossCodes}
        />
      )}
      <div style={{ display: 'flex', flexDirection: 'column', gap: Theme.Size.M, padding: Theme.Size.M }}>
        <CheckWrapper style={{ display: 'flex', alignSelf: 'flex-end' }}>
          <CheckInput
            name="allowManualJobItemsCreation"
            label={lang.allowManualJobItems}
            class={inputStyles.check.switch}
            value={form.data.allowManualJobItemsCreation}
            onChange={({ allowManualJobItemsCreation }) => {
              form.update({
                allowManualJobItemsCreation: Boolean(allowManualJobItemsCreation),
              });
            }}
            htmlElementProps={{ title: form.data.allowManualJobItemsCreation ? lang.disable : lang.enable }}
          />
        </CheckWrapper>
        <table className={ThemeTableSimple}>
          <thead>
            <tr>
              <th>{lang.lossCode}</th>
              <th>{lang.correction}</th>
              <th>
                <Button
                  class={ThemeButtonCircle}
                  media={<Icon class="fas-plus" />}
                  active
                  data={{}}
                  disabled={form.data.allowManualJobItemsCreation}
                  onClick={setCurrentJobItemTemplate}
                />
              </th>
            </tr>
          </thead>
          <tbody>
            <DataMap
              data={form.data?.claimTypeJobItemTemplates}
              render={({ data: jobItem, index }) => {
                const firstBatch = jobItem.lossCodes.slice(0, 6);
                const secondBatch = jobItem.lossCodes.slice(6);
                const isExpanded = jobItem.id === expandedJobItemId;
                return (
                  <tr key={`${jobItem?.id}_${index}`}>
                    <td>
                      <TextBox
                        title={getLineItemLabel(jobItem.jobItemTypeCode)}
                        subtitle={
                          <>
                            <DataMap
                              data={firstBatch}
                              render={({ data }) => (
                                <Box
                                  key={data.id}
                                  class={ThemeTagRounded}
                                  render={
                                    currentLanguage === Language.english ? data.descnEn : (data.descFr ?? data.descnEn)
                                  }
                                />
                              )}
                            />
                            {isExpanded && (
                              <DataMap
                                data={secondBatch}
                                render={({ data }) => (
                                  <Box
                                    key={data.id}
                                    class={ThemeTagRounded}
                                    render={
                                      currentLanguage === Language.english ? data.descnEn : (data.descFr ?? data.descnEn)
                                    }
                                  />
                                )}
                              />
                            )}
                            {secondBatch.length > 0 && (
                              <div
                                className={ThemeTagRounded}
                                style={{
                                  backgroundColor: Theme.Color.neutral,
                                  color: Theme.Color.neutralAlt,
                                  cursor: 'pointer',
                                }}
                                onClick={() => setExpandedJobItemId(isExpanded ? null : jobItem.id)}
                              >
                                {isExpanded ? (
                                  lang.viewLess
                                ) : (
                                  <>
                                    {lang.viewMore} (+{secondBatch.length})
                                  </>
                                )}
                              </div>
                            )}
                          </>
                        }
                        class={{
                          textBox: textBoxStyles.block.tagBlock,
                        }}
                        style={{
                          textBox: {
                            padding: 0,
                          },
                        }}
                      />
                    </td>
                    <td>{jobItem.correction}</td>
                    <td>
                      <Button
                        class={ThemeButtonCircle}
                        media={<Icon class="fas-edit" />}
                        data={jobItem}
                        onClick={setCurrentJobItemTemplate}
                      />
                      <Button
                        class={ThemeButtonCircle}
                        media={<Icon class="fas-trash" />}
                        data={index}
                        onClick={deleteJobItemTemplate}
                      />
                    </td>
                  </tr>
                );
              }}
            />
          </tbody>
        </table>
      </div>
    </>
  );
}
